/**
 * Video Controller - Ensures only one video plays at a time
 * Automatically pauses other videos when a new video starts playing
 */
(function () {
    "use strict";

    class VideoController {
        constructor() {
            this.videos = [];
            this.currentlyPlaying = null;
            this.init();
        }

        init() {
            // Wait for DOM to be ready
            if (document.readyState === "loading") {
                document.addEventListener("DOMContentLoaded", () => this.setupVideoControls());
            } else {
                this.setupVideoControls();
            }

            // Also handle dynamically added videos
            this.setupMutationObserver();
        }

        setupVideoControls() {
            // Get all video elements on the page
            this.videos = Array.from(document.querySelectorAll("video"));

            // Add event listeners to existing videos
            this.videos.forEach((video) => this.addVideoListeners(video));
        }

        setupMutationObserver() {
            // Watch for dynamically added video elements
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Check if the added node is a video or contains videos
                            if (node.tagName === "VIDEO") {
                                this.addVideoToController(node);
                            } else if (node.querySelectorAll) {
                                const videos = node.querySelectorAll("video");
                                videos.forEach((video) => this.addVideoToController(video));
                            }
                        }
                    });
                });
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }

        addVideoToController(video) {
            if (!this.videos.includes(video)) {
                this.videos.push(video);
                this.addVideoListeners(video);
            }
        }

        addVideoListeners(video) {
            // Remove existing listeners to prevent duplicates
            video.removeEventListener("play", this.handleVideoPlay.bind(this));
            video.removeEventListener("pause", this.handleVideoPause.bind(this));
            video.removeEventListener("ended", this.handleVideoEnded.bind(this));

            // Add event listeners
            video.addEventListener("play", this.handleVideoPlay.bind(this));
            video.addEventListener("pause", this.handleVideoPause.bind(this));
            video.addEventListener("ended", this.handleVideoEnded.bind(this));
        }

        handleVideoPlay(event) {
            const playingVideo = event.target;

            // Pause all other videos
            this.pauseAllExcept(playingVideo);

            // Update currently playing video
            this.currentlyPlaying = playingVideo;

            // Optional: Add playing class for styling
            playingVideo.classList.add("video-playing");

            // Dispatch custom event
            document.dispatchEvent(
                new CustomEvent("videoStarted", {
                    detail: { video: playingVideo },
                }),
            );
        }

        handleVideoPause(event) {
            const video = event.target;

            // Remove playing class
            video.classList.remove("video-playing");

            // Clear currently playing if this was the playing video
            if (this.currentlyPlaying === video) {
                this.currentlyPlaying = null;
            }

            // Dispatch custom event
            document.dispatchEvent(
                new CustomEvent("videoPaused", {
                    detail: { video: video },
                }),
            );
        }

        handleVideoEnded(event) {
            const video = event.target;

            // Remove playing class
            video.classList.remove("video-playing");

            // Clear currently playing
            this.currentlyPlaying = null;

            // Dispatch custom event
            document.dispatchEvent(
                new CustomEvent("videoEnded", {
                    detail: { video: video },
                }),
            );
        }

        pauseAllExcept(excludeVideo) {
            this.videos.forEach((video) => {
                if (video !== excludeVideo && !video.paused) {
                    video.pause();
                }
            });
        }

        pauseAll() {
            this.videos.forEach((video) => {
                if (!video.paused) {
                    video.pause();
                }
            });
            this.currentlyPlaying = null;
        }
        // Method to manually refresh video list
        refreshVideos() {
            this.setupVideoControls();
        }
    }

    // Create global instance
    window.VideoController = new VideoController();
})();
